import React, { useState } from 'react'
import { useTranslation } from 'next-i18next'
import { twMerge } from 'tailwind-merge'
import { PrimaryButton } from 'shared/components/button'
import { Tooltip } from 'shared/components/tooltip'
import DocumentsIcon from 'shared/icons/documents-icon'
import { TemplatesFilterInterface } from '../types/templates-filter-interface'
import { TemplatesInterface } from '../types/templates-interface'

interface TemplatesListItemProps {
  template: TemplatesInterface
  onChangeCategory: (category: number | null) => void
  selectedCategory: TemplatesFilterInterface['category']
}

const TemplatesListItem = ({
  template,
  onChangeCategory,
  selectedCategory,
}: TemplatesListItemProps) => {
  const { t } = useTranslation()

  const [isButtonFocused, setIsButtonFocused] = useState(false)

  const handleButtonFocus = () => {
    setIsButtonFocused(true)
  }

  const handleButtonBlur = () => {
    setIsButtonFocused(false)
  }

  return (
    <div className="group flex flex-col overflow-x-hidden rounded-lg shadow-xl">
      <div className="relative aspect-video overflow-hidden">
        <img
          className="w-[-webkit-fill-available]"
          alt={template.preview?.filename}
          src={template.preview?.path}
        />
        {template.hasMultipleSteps && (
          <Tooltip
            mode={'hover'}
            className={'!absolute left-3 top-3 z-10'}
            label={t('public.templates.funnel_template')}
            tooltipClassName={'w-max'}
          >
            <div className={'rounded-lg bg-text-primary p-2'}>
              <DocumentsIcon />
            </div>
          </Tooltip>
        )}
        <div
          className={twMerge(
            'absolute inset-0 flex size-full items-center justify-center gap-3 bg-text-primary/0 transition-all duration-200 ease-linear group-hover:bg-text-primary/50 group-hover:backdrop-blur-sm',
            isButtonFocused && 'bg-text-primary/50 backdrop-blur-sm',
          )}
        >
          {template.previewUrl && (
            <PrimaryButton
              onFocus={handleButtonFocus}
              onBlur={handleButtonBlur}
              onMouseLeave={handleButtonBlur}
              className={twMerge(
                'transition-opacity ease-linear',
                isButtonFocused && 'opacity-100',
                'opacity-0 duration-200 group-hover:opacity-100',
              )}
              href={template.previewUrl}
              target={'_blank'}
            >
              {t('public.templates.preview')}
            </PrimaryButton>
          )}
          <PrimaryButton
            onFocus={handleButtonFocus}
            onBlur={handleButtonBlur}
            onMouseLeave={handleButtonBlur}
            className={twMerge(
              'transition-opacity duration-200 ease-linear',
              isButtonFocused && 'opacity-100',
              'opacity-0 group-hover:opacity-100',
            )}
            href={`/funnel/share/${template.hash}`}
            target={'_self'}
            locale={false}
          >
            {t('public.templates.select')}
          </PrimaryButton>
        </div>
      </div>
      <div className={'flex flex-col gap-3 px-3 py-6'}>
        {template.categories.length !== 0 && (
          <div className={'flex flex-wrap gap-1.5'}>
            {template.categories.map(category => (
              <button
                disabled={selectedCategory === category.id}
                onClick={() => onChangeCategory(category.id)}
                key={category.id}
                className={twMerge(
                  'main-transition-colors outline-none',
                  selectedCategory !== category.id && 'hover:text-brand-primary',
                  'rounded-md bg-background-tertiary/30 px-2 py-1.5 text-[13px] text-text-secondary ring-brand-primary ring-offset-2 focus-visible:ring-2',
                )}
              >
                {category.name}
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}

export default TemplatesListItem
